import {
  CABLE_BASE_CAPACITY,
  CABLE_JUNCTION_CAPACITY,
  CROSSING_PENALTY,
  INTERFERENCE_SPEED_MULT,
  MAGNETIC_SPEED_MULT,
  MIN_THROUGHPUT,
  PULSE_MEETING_SPEED_MULT,
} from '../config/balance.js';
import type { ColorId } from '../config/colors.js';
import { cellKey, cellsAdjacent, isMagnetic, nodePortCell } from './grid.js';
import type { Cable, CellCoord, GameNode, GameState, Pulse, RunModifiers } from './types.js';

export interface EdgeKey {
  a: string;
  b: string;
}

export function edgeKeyFromCells(c1: CellCoord, c2: CellCoord): string {
  const k1 = cellKey(c1);
  const k2 = cellKey(c2);
  return k1 < k2 ? `${k1}|${k2}` : `${k2}|${k1}`;
}

export interface NetworkAnalysis {
  /** cell key -> cable ids passing through */
  cellCableCounts: Map<string, string[]>;
  /** edge key -> throughput multiplier (0..1] from crossings */
  edgeThroughputMult: Map<string, number>;
  /** adjacency: cell key -> neighbor cell keys via cables */
  adjacency: Map<string, string[]>;
  /** cell key -> node id if relay or building connection cell */
  cellToNode: Map<string, string>;
  /** Cells with multi-color interference (no relay). */
  interferenceCellKeys: Set<string>;
  /** Relay cell keys (fast cancel check). */
  relayCellKeys: Set<string>;
  /** Switch cell keys — crossings stay electrically separate. */
  switchCellKeys: Set<string>;
  /** Generator port cell → color (foreign colors cannot transit). */
  generatorColorByCell: Map<string, ColorId>;
  /** Precomputed feed color per cable id. */
  feedColorByCableId: Map<string, ColorId | null>;
  /** cable id → Cable (avoid repeated finds). */
  cableById: Map<string, Cable>;
}

function buildPortColorMap(state: GameState): Map<string, ColorId> {
  const portColor = new Map<string, ColorId>();
  for (const n of state.nodes) {
    if (!n.color || (n.kind !== 'generator' && n.kind !== 'bulb')) continue;
    portColor.set(cellKey(nodePortCell(n)), n.color);
  }
  return portColor;
}

function buildNodeById(state: GameState): Map<string, GameNode> {
  return new Map(state.nodes.map((n) => [n.id, n]));
}

/** Colors on this cable alone: endpoints + gen/bulb ports on its cells. */
function cableLocalColors(
  state: GameState,
  cable: Cable,
  portColor?: Map<string, ColorId>,
  nodeById?: Map<string, GameNode>,
): ColorId[] {
  const colors = new Set<ColorId>();
  const nodes = nodeById ?? buildNodeById(state);
  for (const id of [cable.endpointA, cable.endpointB]) {
    if (!id) continue;
    const n = nodes.get(id);
    if (n?.color && (n.kind === 'generator' || n.kind === 'bulb')) {
      colors.add(n.color);
    }
  }
  const ports = portColor ?? buildPortColorMap(state);
  for (const cell of cable.cells) {
    const col = ports.get(cellKey(cell));
    if (col) colors.add(col);
  }
  return [...colors];
}

/**
 * Color this cable carries.
 * - 1 color (local or via same-color junctions / later links) → that color
 * - 0 or 2+ colors → null (drawn multicolor)
 * With `analysis`, walks neighboring cables but stops at conflicting colors
 * so crossings stay detectable for interference.
 */
export function cableFeedColor(
  state: GameState,
  cable: Cable,
  analysis?: NetworkAnalysis,
): ColorId | null {
  if (analysis?.feedColorByCableId.has(cable.id)) {
    return analysis.feedColorByCableId.get(cable.id) ?? null;
  }
  if (!analysis) {
    const local = cableLocalColors(state, cable);
    if (local.length === 1) return local[0]!;
    return null;
  }

  const byId = analysis.cableById;
  const portColor = buildPortColorMap(state);
  const nodeById = buildNodeById(state);
  const localCache = new Map<string, ColorId[]>();
  const localOf = (cab: Cable) => {
    let loc = localCache.get(cab.id);
    if (!loc) {
      loc = cableLocalColors(state, cab, portColor, nodeById);
      localCache.set(cab.id, loc);
    }
    return loc;
  };

  const visited = new Set<string>();
  const queue: string[] = [cable.id];
  const colors = new Set<ColorId>();

  while (queue.length > 0) {
    const id = queue.pop()!;
    if (visited.has(id)) continue;
    visited.add(id);
    const cab = byId.get(id);
    if (!cab) continue;

    for (const col of localOf(cab)) colors.add(col);

    for (const cell of cab.cells) {
      const ck = cellKey(cell);
      // Switch keeps crossing networks electrically separate — no feed merge.
      if (analysis.switchCellKeys.has(ck)) continue;
      for (const oid of analysis.cellCableCounts.get(ck) ?? []) {
        if (visited.has(oid)) continue;
        const other = byId.get(oid);
        if (!other) continue;
        const theirs = localOf(other);
        // Do not absorb a cable that introduces a foreign color (crossing).
        if (colors.size > 0 && theirs.some((c) => !colors.has(c))) continue;
        queue.push(oid);
      }
    }
  }

  if (colors.size === 1) return [...colors][0]!;
  return null;
}

/** Distinct feed colors of cables through a cell (ignores relays). */
export function colorsAtCell(
  state: GameState,
  analysis: NetworkAnalysis,
  c: CellCoord,
): ColorId[] {
  const ids = analysis.cellCableCounts.get(cellKey(c)) ?? [];
  const colors = new Set<ColorId>();
  for (const id of ids) {
    const col =
      analysis.feedColorByCableId.get(id) ??
      (() => {
        const cable = analysis.cableById.get(id);
        return cable ? cableFeedColor(state, cable, analysis) : null;
      })();
    if (col) colors.add(col);
  }
  return [...colors];
}

/**
 * True when 2+ cables of different colors share the cell and no relay
 * cancels the interference.
 */
export function hasColorInterferenceAt(
  _state: GameState,
  analysis: NetworkAnalysis,
  c: CellCoord,
): boolean {
  const k = cellKey(c);
  if (analysis.relayCellKeys.has(k)) return false;
  return analysis.interferenceCellKeys.has(k);
}

/** All cells currently suffering color-crossing interference. */
export function listInterferenceCells(
  _state: GameState,
  analysis: NetworkAnalysis,
): CellCoord[] {
  const out: CellCoord[] = [];
  for (const k of analysis.interferenceCellKeys) {
    const [xs, ys] = k.split(',').map(Number);
    out.push({ x: xs!, y: ys! });
  }
  return out;
}

/** Only the dedicated port cell is a valid cable attachment point. */
export function buildingConnectionCells(node: GameNode): CellCoord[] {
  return [nodePortCell(node)];
}

export function buildNetworkAnalysis(state: GameState): NetworkAnalysis {
  const cellCableCounts = new Map<string, string[]>();
  const adjacency = new Map<string, string[]>();
  const cellToNode = new Map<string, string>();
  const relayCellKeys = new Set<string>();
  const switchCellKeys = new Set<string>();
  const generatorColorByCell = new Map<string, ColorId>();
  const cableById = new Map(state.cables.map((c) => [c.id, c]));
  const nodeById = buildNodeById(state);

  for (const node of state.nodes) {
    for (const c of buildingConnectionCells(node)) {
      cellToNode.set(cellKey(c), node.id);
    }
    if (node.kind === 'relay') {
      relayCellKeys.add(cellKey(node.anchor));
    }
    if (node.kind === 'switch') {
      switchCellKeys.add(cellKey(node.anchor));
    }
    // Generators AND bulbs: foreign-color ports block transit (goal cells still OK).
    if (
      (node.kind === 'generator' || node.kind === 'bulb') &&
      node.color
    ) {
      generatorColorByCell.set(cellKey(nodePortCell(node)), node.color);
    }
  }

  for (const cable of state.cables) {
    for (const c of cable.cells) {
      const k = cellKey(c);
      const list = cellCableCounts.get(k) ?? [];
      if (!list.includes(cable.id)) list.push(cable.id);
      cellCableCounts.set(k, list);
    }
    for (let i = 0; i < cable.cells.length - 1; i++) {
      const a = cellKey(cable.cells[i]!);
      const b = cellKey(cable.cells[i + 1]!);
      const na = adjacency.get(a) ?? [];
      if (!na.includes(b)) na.push(b);
      adjacency.set(a, na);
      const nb = adjacency.get(b) ?? [];
      if (!nb.includes(a)) nb.push(a);
      adjacency.set(b, nb);
    }
  }

  // Linked teleporters form a bidirectional wormhole in the graph.
  for (const node of state.nodes) {
    if (node.kind !== 'teleporter' || !node.linkedNodeId) continue;
    if (node.id > node.linkedNodeId) continue; // handle each pair once
    const other = nodeById.get(node.linkedNodeId);
    if (!other || other.kind !== 'teleporter') continue;
    const a = cellKey(node.anchor);
    const b = cellKey(other.anchor);
    const na = adjacency.get(a) ?? [];
    if (!na.includes(b)) na.push(b);
    adjacency.set(a, na);
    const nb = adjacency.get(b) ?? [];
    if (!nb.includes(a)) nb.push(a);
    adjacency.set(b, nb);
  }

  // Partial analysis so cableFeedColor can reuse maps while computing feeds.
  const partial: NetworkAnalysis = {
    cellCableCounts,
    edgeThroughputMult: new Map(),
    adjacency,
    cellToNode,
    interferenceCellKeys: new Set(),
    relayCellKeys,
    switchCellKeys,
    generatorColorByCell,
    feedColorByCableId: new Map(),
    cableById,
  };

  const feedColorByCableId = new Map<string, ColorId | null>();
  for (const cable of state.cables) {
    // Temporarily empty map forces compute path; then we store.
    const col = cableFeedColor(state, cable, {
      ...partial,
      feedColorByCableId, // growing memo — already-computed cables reuse
    });
    feedColorByCableId.set(cable.id, col);
  }
  partial.feedColorByCableId = feedColorByCableId;

  const interferenceCellKeys = new Set<string>();
  for (const [k, ids] of cellCableCounts) {
    if (ids.length < 2 || relayCellKeys.has(k)) continue;
    const colors = new Set<ColorId>();
    for (const id of ids) {
      const col = feedColorByCableId.get(id);
      if (col) colors.add(col);
    }
    if (colors.size > 1) interferenceCellKeys.add(k);
  }
  partial.interferenceCellKeys = interferenceCellKeys;

  const edgeThroughputMult = new Map<string, number>();
  const resist = state.runModifiers.interferenceResist;

  for (const cable of state.cables) {
    for (let i = 0; i < cable.cells.length - 1; i++) {
      const c1 = cable.cells[i]!;
      const c2 = cable.cells[i + 1]!;
      const ek = edgeKeyFromCells(c1, c2);
      if (edgeThroughputMult.has(ek)) continue;

      let mult = 1;
      for (const c of [c1, c2]) {
        const k = cellKey(c);
        if (interferenceCellKeys.has(k)) {
          // Approximate crossings from distinct feed colors at the cell.
          const colors = new Set<ColorId>();
          for (const id of cellCableCounts.get(k) ?? []) {
            const col = feedColorByCableId.get(id);
            if (col) colors.add(col);
          }
          const crossings = Math.max(0, colors.size - 1);
          for (let x = 0; x < crossings; x++) {
            mult *= 1 - CROSSING_PENALTY * (1 - resist);
          }
        }
      }
      edgeThroughputMult.set(ek, Math.max(MIN_THROUGHPUT, mult));
    }
  }
  partial.edgeThroughputMult = edgeThroughputMult;

  return partial;
}

function edgeTouchesJunction(
  analysis: NetworkAnalysis,
  c1: CellCoord,
  c2: CellCoord,
): boolean {
  const k1 = cellKey(c1);
  const k2 = cellKey(c2);
  return (
    analysis.relayCellKeys.has(k1) ||
    analysis.relayCellKeys.has(k2) ||
    analysis.switchCellKeys.has(k1) ||
    analysis.switchCellKeys.has(k2)
  );
}

export function edgeBaseCapacity(
  mods: RunModifiers,
  junctionBoost = false,
): number {
  const cap = junctionBoost ? CABLE_JUNCTION_CAPACITY : CABLE_BASE_CAPACITY;
  return cap * mods.globalThroughputMult * (1 + mods.edgeThroughputBonus);
}

export function edgeEffectiveThroughput(
  analysis: NetworkAnalysis,
  c1: CellCoord,
  c2: CellCoord,
  mods: RunModifiers,
): number {
  const ek = edgeKeyFromCells(c1, c2);
  const crossMult = analysis.edgeThroughputMult.get(ek) ?? 1;
  const junction = edgeTouchesJunction(analysis, c1, c2);
  return Math.max(MIN_THROUGHPUT, edgeBaseCapacity(mods, junction) * crossMult);
}

/**
 * Grid cell a pulse currently occupies (nearest cell along its segment).
 */
export function pulseOccupancyCell(
  state: GameState,
  pulse: Pulse,
  cableById?: Map<string, Cable>,
): CellCoord | null {
  const cable = cableById?.get(pulse.cableId) ?? state.cables.find((c) => c.id === pulse.cableId);
  if (!cable) return null;
  const a = cable.cells[pulse.segmentIndex];
  const b = cable.cells[pulse.segmentIndex + 1];
  if (!a || !b) return null;
  const forward = pulse.forward !== false;
  const from = forward ? a : b;
  const to = forward ? b : a;
  const t = Math.max(0, Math.min(1, pulse.segmentT));
  return {
    x: Math.round(from.x + (to.x - from.x) * t),
    y: Math.round(from.y + (to.y - from.y) * t),
  };
}

/** Cell keys currently occupied by 2+ pulses (O(n) build). */
export function buildCrowdedPulseCells(
  state: GameState,
  cableById?: Map<string, Cable>,
): Set<string> {
  const counts = new Map<string, number>();
  for (const pulse of state.pulses) {
    const cell = pulseOccupancyCell(state, pulse, cableById);
    if (!cell) continue;
    const k = cellKey(cell);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const crowded = new Set<string>();
  for (const [k, n] of counts) {
    if (n >= 2) crowded.add(k);
  }
  return crowded;
}

/** True when another pulse shares this pulse's current cell (any colors). */
export function pulseIsMeeting(
  state: GameState,
  pulse: Pulse,
  crowdedCells?: Set<string>,
  cableById?: Map<string, Cable>,
): boolean {
  const cell = pulseOccupancyCell(state, pulse, cableById);
  if (!cell) return false;
  const k = cellKey(cell);
  if (crowdedCells) return crowdedCells.has(k);
  // Slow fallback (tests / rare callers).
  for (const other of state.pulses) {
    if (other.id === pulse.id) continue;
    const oc = pulseOccupancyCell(state, other, cableById);
    if (oc && cellKey(oc) === k) return true;
  }
  return false;
}

export function segmentSpeedMult(
  state: GameState,
  c1: CellCoord,
  c2: CellCoord,
  analysis?: NetworkAnalysis,
  meeting = false,
): number {
  let mult = state.runModifiers.pulseSpeedMult;
  const mid = { x: (c1.x + c2.x) / 2, y: (c1.y + c2.y) / 2 };
  const cMid = { x: Math.floor(mid.x), y: Math.floor(mid.y) };
  if (isMagnetic(state.terrain, c1) || isMagnetic(state.terrain, c2) || isMagnetic(state.terrain, cMid)) {
    const shield = state.runModifiers.magneticResist;
    mult *= MAGNETIC_SPEED_MULT + (1 - MAGNETIC_SPEED_MULT) * shield;
  }
  if (analysis) {
    const resist = state.runModifiers.interferenceResist;
    const hit =
      hasColorInterferenceAt(state, analysis, c1) ||
      hasColorInterferenceAt(state, analysis, c2);
    if (hit) {
      // ~40% speed (noticeable); resist upgrades ease back toward full speed.
      mult *= INTERFERENCE_SPEED_MULT + (1 - INTERFERENCE_SPEED_MULT) * resist;
    }
  }
  if (meeting) {
    mult *= PULSE_MEETING_SPEED_MULT;
  }
  return mult;
}

export function hasCrossingAt(
  state: GameState,
  analysis: NetworkAnalysis,
  c: CellCoord,
): boolean {
  return hasColorInterferenceAt(state, analysis, c);
}

export function crossingPenaltyMult(
  analysis: NetworkAnalysis,
  c: CellCoord,
): number {
  const k = cellKey(c);
  return analysis.edgeThroughputMult.get(k) ?? 1;
}

/**
 * True when from→via→to stays on a single cable (required at Switch cells).
 * If `from` is null (starting on the switch), any same-cable neighbor is OK.
 */
export function switchAllowsStep(
  analysis: NetworkAnalysis,
  from: CellCoord | null,
  via: CellCoord,
  to: CellCoord,
): boolean {
  const viaK = cellKey(via);
  if (!analysis.switchCellKeys.has(viaK)) return true;
  const toK = cellKey(to);
  const fromK = from ? cellKey(from) : null;
  for (const id of analysis.cellCableCounts.get(viaK) ?? []) {
    const cable = analysis.cableById.get(id);
    if (!cable) continue;
    for (let i = 0; i < cable.cells.length; i++) {
      if (cellKey(cable.cells[i]!) !== viaK) continue;
      const prev = i > 0 ? cellKey(cable.cells[i - 1]!) : null;
      const next =
        i < cable.cells.length - 1 ? cellKey(cable.cells[i + 1]!) : null;
      if (!fromK) {
        if (toK === prev || toK === next) return true;
      } else if (
        (fromK === prev && toK === next) ||
        (fromK === next && toK === prev)
      ) {
        return true;
      }
    }
  }
  return false;
}

/** Foreign-color generator/bulb ports cannot be entered by another color. */
export function isForeignGeneratorCell(
  analysis: NetworkAnalysis,
  cellK: string,
  pulseColor: ColorId | null | undefined,
): boolean {
  if (!pulseColor) return false;
  const portColor = analysis.generatorColorByCell.get(cellK);
  return !!portColor && portColor !== pulseColor;
}

function cableContainsEdge(cable: Cable, a: CellCoord, b: CellCoord): boolean {
  for (let i = 0; i < cable.cells.length - 1; i++) {
    const c1 = cable.cells[i]!;
    const c2 = cable.cells[i + 1]!;
    if (
      (c1.x === a.x && c1.y === a.y && c2.x === b.x && c2.y === b.y) ||
      (c2.x === a.x && c2.y === a.y && c1.x === b.x && c1.y === b.y)
    ) {
      return true;
    }
  }
  return false;
}

function isCableEndpointCell(cable: Cable, cell: CellCoord): boolean {
  if (cable.cells.length === 0) return false;
  const first = cable.cells[0]!;
  const last = cable.cells[cable.cells.length - 1]!;
  return (
    (first.x === cell.x && first.y === cell.y) ||
    (last.x === cell.x && last.y === cell.y)
  );
}

/**
 * True when a same-color cable T-junctions onto `bridgeId` (spur ends on the
 * bridge). A pure crossing (both cables continue through the cell) does NOT
 * count — that keeps networks electrically separate without a switch.
 */
export function cableIsColorTJunctionBridge(
  analysis: NetworkAnalysis,
  bridgeId: string,
  pulseColor: ColorId,
): boolean {
  const bridge = analysis.cableById.get(bridgeId);
  if (!bridge) return false;
  for (const cell of bridge.cells) {
    const ck = cellKey(cell);
    if (analysis.switchCellKeys.has(ck)) continue;
    for (const oid of analysis.cellCableCounts.get(ck) ?? []) {
      if (oid === bridgeId) continue;
      const other = analysis.cableById.get(oid);
      if (!other) continue;
      const feed = analysis.feedColorByCableId.get(oid);
      if (feed !== pulseColor) continue;
      // Spur of our color ends here onto the bridge → shared trunk allowed.
      if (isCableEndpointCell(other, cell)) return true;
    }
  }
  return false;
}

/**
 * An edge is usable by `pulseColor` if it belongs to:
 * - a cable of that color / unowned (null feed), or
 * - a foreign cable that a same-color spur T-junctions onto (shared trunk).
 * Pure geometric crossings stay blocked (no hop between parallel networks).
 */
export function edgeAllowsPulseColor(
  analysis: NetworkAnalysis,
  a: CellCoord,
  b: CellCoord,
  pulseColor?: ColorId | null,
): boolean {
  if (!pulseColor) return true;
  let sawCableEdge = false;
  for (const id of analysis.cellCableCounts.get(cellKey(a)) ?? []) {
    const cable = analysis.cableById.get(id);
    if (!cable || !cableContainsEdge(cable, a, b)) continue;
    sawCableEdge = true;
    const feed = analysis.feedColorByCableId.get(id);
    if (feed == null || feed === pulseColor) return true;
    if (cableIsColorTJunctionBridge(analysis, id, pulseColor)) return true;
  }
  // No cable owns this adjacency → teleporter wormhole (allowed).
  return !sawCableEdge;
}

/** BFS from start cell keys to all reachable cell keys (color + switch aware). */
export function bfsReachable(
  analysis: NetworkAnalysis,
  starts: string[],
  pulseColor?: ColorId | null,
): Set<string> {
  const seen = new Set<string>();
  /** Queue entries remember previous cell for switch continuity. */
  const queue: { key: string; prev: string | null }[] = [];
  for (const s of starts) {
    if (isForeignGeneratorCell(analysis, s, pulseColor)) continue;
    seen.add(s);
    queue.push({ key: s, prev: null });
  }
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const via = parseKey(cur.key);
    const from = cur.prev ? parseKey(cur.prev) : null;
    for (const n of analysis.adjacency.get(cur.key) ?? []) {
      if (seen.has(n)) continue;
      if (isForeignGeneratorCell(analysis, n, pulseColor)) continue;
      const to = parseKey(n);
      if (!edgeAllowsPulseColor(analysis, via, to, pulseColor)) continue;
      // Leaving a switch: must stay on the same cable.
      if (!switchAllowsStep(analysis, from, via, to)) continue;
      seen.add(n);
      queue.push({ key: n, prev: cur.key });
    }
  }
  return seen;
}

export interface PathStep {
  from: CellCoord;
  to: CellCoord;
  cableId: string;
}

/** Find shortest cell path from startCell to any cell adjacent to target node footprint. */
export function findCellPathToNode(
  _state: GameState,
  analysis: NetworkAnalysis,
  startCell: CellCoord,
  targetNode: GameNode,
  pulseColor?: ColorId | null,
): CellCoord[] | null {
  const goals = new Set<string>();
  for (const c of buildingConnectionCells(targetNode)) {
    goals.add(cellKey(c));
  }

  const startK = cellKey(startCell);
  if (isForeignGeneratorCell(analysis, startK, pulseColor) && !goals.has(startK)) {
    return null;
  }
  if (goals.has(startK)) return [startCell];

  const prev = new Map<string, string | null>();
  const queue: string[] = [startK];
  prev.set(startK, null);

  while (queue.length > 0) {
    const cur = queue.shift()!;
    if (goals.has(cur)) {
      const keys: string[] = [];
      let k: string | null = cur;
      while (k) {
        keys.push(k);
        k = prev.get(k) ?? null;
      }
      keys.reverse();
      return keys.map(parseKey);
    }

    const via = parseKey(cur);
    const fromK = prev.get(cur) ?? null;
    const from = fromK ? parseKey(fromK) : null;
    for (const nk of analysis.adjacency.get(cur) ?? []) {
      if (prev.has(nk)) continue;
      if (isForeignGeneratorCell(analysis, nk, pulseColor) && !goals.has(nk)) {
        continue;
      }
      const to = parseKey(nk);
      if (!edgeAllowsPulseColor(analysis, via, to, pulseColor)) continue;
      if (!switchAllowsStep(analysis, from, via, to)) continue;
      prev.set(nk, cur);
      queue.push(nk);
    }
  }

  return null;
}

function parseKey(k: string): CellCoord {
  const [xs, ys] = k.split(',');
  return { x: Number(xs), y: Number(ys) };
}

function findCableBetween(cables: Cable[], a: CellCoord, b: CellCoord): string | null {
  for (const cable of cables) {
    for (let i = 0; i < cable.cells.length - 1; i++) {
      const c1 = cable.cells[i]!;
      const c2 = cable.cells[i + 1]!;
      if (
        (c1.x === a.x && c1.y === a.y && c2.x === b.x && c2.y === b.y) ||
        (c2.x === a.x && c2.y === a.y && c1.x === b.x && c1.y === b.y)
      ) {
        return cable.id;
      }
    }
  }
  return null;
}

/** Synthetic route step: pulse warps from cell A to cell B. */
export function teleportRouteId(from: CellCoord, to: CellCoord): string {
  return `tp:${cellKey(from)}>${cellKey(to)}`;
}

export function isTeleportRouteId(id: string): boolean {
  return id.startsWith('tp:');
}

export function parseTeleportRoute(
  id: string,
): { from: CellCoord; to: CellCoord } | null {
  if (!isTeleportRouteId(id)) return null;
  const body = id.slice(3);
  const sep = body.indexOf('>');
  if (sep < 0) return null;
  const [fx, fy] = body.slice(0, sep).split(',').map(Number);
  const [tx, ty] = body.slice(sep + 1).split(',').map(Number);
  if (![fx, fy, tx, ty].every((n) => Number.isFinite(n))) return null;
  return { from: { x: fx!, y: fy! }, to: { x: tx!, y: ty! } };
}

export function isTeleporterJump(
  state: GameState,
  a: CellCoord,
  b: CellCoord,
): boolean {
  const na = state.nodes.find(
    (n) =>
      n.kind === 'teleporter' &&
      n.anchor.x === a.x &&
      n.anchor.y === a.y &&
      n.linkedNodeId,
  );
  if (!na) return false;
  const nb = state.nodes.find((n) => n.id === na.linkedNodeId);
  return !!nb && nb.kind === 'teleporter' && nb.anchor.x === b.x && nb.anchor.y === b.y;
}

export function pathToCableRoute(
  path: CellCoord[],
  cables: Cable[],
  state?: GameState,
): string[] | null {
  if (path.length < 2) return [];
  const route: string[] = [];
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i]!;
    const b = path[i + 1]!;
    let id = findCableBetween(cables, a, b);
    if (!id && state && isTeleporterJump(state, a, b)) {
      id = teleportRouteId(a, b);
    }
    if (!id) return null;
    if (route[route.length - 1] !== id) route.push(id);
  }
  return route;
}

export function generatorStartCell(node: GameNode, cable: Cable): CellCoord | null {
  for (const c of buildingConnectionCells(node)) {
    if (cable.cells.some((p) => p.x === c.x && p.y === c.y)) return c;
  }
  return cable.cells[0] ?? null;
}

/**
 * Orient travel on a cable so the pulse leaves `fromCell` toward the other end
 * (always generator → bulb visually, regardless of draw order).
 */
export function orientCableFromCell(
  cable: Cable,
  fromCell: CellCoord,
): { segmentIndex: number; forward: boolean } {
  if (cable.cells.length < 2) return { segmentIndex: 0, forward: true };
  const first = cable.cells[0]!;
  const last = cable.cells[cable.cells.length - 1]!;
  const dFirst = Math.abs(first.x - fromCell.x) + Math.abs(first.y - fromCell.y);
  const dLast = Math.abs(last.x - fromCell.x) + Math.abs(last.y - fromCell.y);
  if (dFirst <= dLast) {
    return { segmentIndex: 0, forward: true };
  }
  return { segmentIndex: cable.cells.length - 2, forward: false };
}

/**
 * Enter a cable at a specific cell (incl. mid-cable T-junctions).
 * When `towardCell` is set, pick the direction that approaches it.
 */
export function enterCableAtCell(
  cable: Cable,
  fromCell: CellCoord,
  towardCell?: CellCoord | null,
): { segmentIndex: number; forward: boolean } {
  if (cable.cells.length < 2) return { segmentIndex: 0, forward: true };

  const idx = cable.cells.findIndex(
    (c) => c.x === fromCell.x && c.y === fromCell.y,
  );
  if (idx < 0) return orientCableFromCell(cable, fromCell);

  if (idx <= 0) return { segmentIndex: 0, forward: true };
  if (idx >= cable.cells.length - 1) {
    return { segmentIndex: cable.cells.length - 2, forward: false };
  }

  // Mid-cable: choose direction toward the destination when known.
  if (towardCell) {
    const next = cable.cells[idx + 1]!;
    const prev = cable.cells[idx - 1]!;
    const dFwd =
      Math.abs(next.x - towardCell.x) + Math.abs(next.y - towardCell.y);
    const dBwd =
      Math.abs(prev.x - towardCell.x) + Math.abs(prev.y - towardCell.y);
    if (dFwd <= dBwd) return { segmentIndex: idx, forward: true };
    return { segmentIndex: idx - 1, forward: false };
  }

  // Default: continue toward the farther end (prefer longer remaining path).
  const toEnd = cable.cells.length - 1 - idx;
  if (toEnd >= idx) return { segmentIndex: idx, forward: true };
  return { segmentIndex: idx - 1, forward: false };
}

export function isNodeReachableFromGenerator(
  state: GameState,
  analysis: NetworkAnalysis,
  generator: GameNode,
  target: GameNode,
): boolean {
  const genCells = buildingConnectionCells(generator)
    .map(cellKey)
    .filter((k) => analysis.adjacency.has(k));
  if (genCells.length === 0) return false;
  const reachable = bfsReachable(analysis, genCells, generator.color ?? null);
  for (const c of buildingConnectionCells(target)) {
    if (reachable.has(cellKey(c))) return true;
  }
  return false;
}

export function validateAdjacencyPath(cells: CellCoord[]): boolean {
  for (let i = 0; i < cells.length - 1; i++) {
    if (!cellsAdjacent(cells[i]!, cells[i + 1]!)) return false;
  }
  return true;
}
